import { useMemo, useState } from 'react';
import { Award, Check, Lock, Share2, Copy, Sparkles } from 'lucide-react';
import { BADGES, type Badge, type BadgeContext } from '@/data/learnBadges';
import { useLearnProgress } from '@/hooks/useLearnProgress';
import { useQuizAttempts } from '@/hooks/useQuizAttempts';
import { LEARN_PATHS, pathProgress } from '@/data/learnPaths';
import { toast } from 'sonner';

function BadgeCard({ badge, ctx }: { badge: Badge; ctx: BadgeContext }) {
  const earned = badge.isEarned(ctx);
  const pct = badge.progress ? Math.round(badge.progress(ctx) * 100) : earned ? 100 : 0;
  const Icon = badge.icon;
  return (
    <div
      className={`relative rounded-xl border p-3 transition-colors ${
        earned ? 'border-ink-amber/40 bg-ink-amber/5' : 'border-border bg-card'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${badge.tile} ${
            earned ? badge.accent : 'text-muted-foreground opacity-50'
          }`}
        >
          <Icon className="h-6 w-6" />
          {earned && (
            <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-ink-emerald text-background">
              <Check className="h-2.5 w-2.5" />
            </span>
          )}
          {!earned && (
            <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-muted-foreground border border-border">
              <Lock className="h-2.5 w-2.5" />
            </span>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className={`font-display text-sm font-semibold truncate ${earned ? 'text-foreground' : 'text-muted-foreground'}`}>
              {badge.title}
            </h4>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground font-body">
              {badge.category}
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground font-body leading-snug">
            {badge.description}
          </p>
          {!earned && badge.progress && pct > 0 && (
            <div className="mt-2">
              <div className="h-1 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground font-body">{pct}%</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function BadgesPanel() {
  const { isComplete } = useLearnProgress();
  const { attempts } = useQuizAttempts();
  const [busy, setBusy] = useState(false);

  const ctx: BadgeContext = useMemo(() => ({ isComplete, attempts }), [isComplete, attempts]);

  const earned = BADGES.filter((b) => b.isEarned(ctx));
  const locked = BADGES.filter((b) => !b.isEarned(ctx));

  const totalPathPct = useMemo(() => {
    const sum = LEARN_PATHS.reduce((acc, p) => acc + pathProgress(p, isComplete).pct, 0);
    return Math.round(sum / LEARN_PATHS.length);
  }, [isComplete]);

  const buildShareText = () => {
    const lines = [
      `🏆 My Lorcana Illumineer Progress`,
      `${earned.length}/${BADGES.length} badges earned · ${totalPathPct}% across all paths`,
      ...earned.slice(0, 6).map((b) => `✓ ${b.title}`),
      ``,
      `Learn at https://lorescout.com`,
    ];
    return lines.join('\n');
  };

  const handleShare = async () => {
    const text = buildShareText();
    setBusy(true);
    try {
      if (typeof navigator !== 'undefined' && (navigator as any).share) {
        await (navigator as any).share({
          title: 'My Lorcana Learn Progress',
          text,
          url: 'https://lorescout.com',
        });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Progress copied to clipboard');
      }
    } catch (e) {
      // share dismissed — ignore
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildShareText());
      toast.success('Progress copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="space-y-3">
      {/* Hero / share card */}
      <div className="rounded-xl border border-ink-amber/30 bg-gradient-to-br from-ink-amber/10 via-primary/5 to-transparent p-4">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-ink-amber" />
          <h2 className="font-display text-base font-semibold text-foreground">Badges</h2>
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-ink-amber/15 px-2 py-0.5 text-[10px] font-medium text-ink-amber font-body">
            <Sparkles className="h-3 w-3" /> {earned.length}/{BADGES.length}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground font-body leading-relaxed">
          Earn badges by finishing paths, passing quizzes, and exploring every Learn tool. Show off your progress.
        </p>
        <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-ink-amber transition-all"
            style={{ width: `${Math.round((earned.length / BADGES.length) * 100)}%` }}
          />
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleShare}
            disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground font-body hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium text-foreground font-body hover:bg-secondary transition-colors"
          >
            <Copy className="h-3.5 w-3.5" /> Copy
          </button>
        </div>
      </div>

      {earned.length > 0 && (
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-body mb-2 px-1">
            Earned
          </h3>
          <div className="space-y-2">
            {earned.map((b) => (
              <BadgeCard key={b.key} badge={b} ctx={ctx} />
            ))}
          </div>
        </div>
      )}

      {locked.length > 0 && (
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-body mb-2 px-1">
            Locked
          </h3>
          <div className="space-y-2">
            {locked.map((b) => (
              <BadgeCard key={b.key} badge={b} ctx={ctx} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
